import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/**
 * Emergent-managed Google Auth callback handler.
 *
 * Flow:
 *   1. /login redirected to https://auth.emergentagent.com/?redirect=<origin>/auth/callback
 *   2. After successful Google auth, Emergent sends user back here.
 *   3. They include a `session_id` in the URL hash fragment: #session_id=XYZ
 *   4. We POST that session_id to our backend /api/auth/google which calls
 *      the Emergent session-data endpoint (server-side) to exchange it for
 *      user info, and then issues our own JWT.
 */
export default function AuthCallback() {
  const { loginWithGoogleSession } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const extract = () => {
      const hash = window.location.hash || "";
      const cleaned = hash.startsWith("#") ? hash.slice(1) : hash;
      const params = new URLSearchParams(cleaned);
      let sid = params.get("session_id");
      if (!sid) {
        const qparams = new URLSearchParams(window.location.search);
        sid = qparams.get("session_id");
      }
      return sid;
    };

    const sessionId = extract();
    if (!sessionId) {
      setError("Missing session_id in callback URL.");
      return;
    }

    (async () => {
      try {
        // Wipe fragment so the session_id is not retained in browser history
        window.history.replaceState(null, "", window.location.pathname);
        const u = await loginWithGoogleSession(sessionId);
        const pendingNext = sessionStorage.getItem("postAuthNext");
        if (pendingNext) sessionStorage.removeItem("postAuthNext");
        if (u && u.needs_onboarding) {
          navigate("/finish-registration", { replace: true });
        } else {
          navigate(pendingNext || "/dashboard", { replace: true });
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "Google sign-in failed");
      }
    })();
  }, [loginWithGoogleSession, navigate]);

  return (
    <div data-testid="auth-callback-page" className="min-h-screen bg-[var(--bg)] text-zinc-100 flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        {error ? (
          <>
            <h1 className="text-3xl font-bold text-red-400">Sign-in failed</h1>
            <p className="mt-3 text-sm text-zinc-400" data-testid="callback-error">{error}</p>
            <button
              onClick={() => navigate("/login", { replace: true })}
              data-testid="callback-back"
              className="mt-6 px-5 py-2.5 rounded-full bg-[var(--accent)] text-black font-semibold"
            >
              Back to login
            </button>
          </>
        ) : (
          <>
            <div className="text-xs uppercase tracking-[0.4em] text-[var(--accent)]">footguesser</div>
            <h1 className="text-3xl font-bold mt-3">Completing sign-in…</h1>
            <p className="mt-2 text-sm text-zinc-400">Exchanging your Google session.</p>
          </>
        )}
      </div>
    </div>
  );
}
